<template>
  <div class="p-tabs">
    <!-- PrimeVue Tabs shell -->
    <Tabs :value="activeId">
      <TabList>
        <Tab
          v-for="t in tabs"
          :key="t.id"
          :value="t.id"
          as="div"
          class="flex items-center p-tab px-4 py-2"
          :data-testid="`contenttab-${props.dataKey}-tab-${t.id}`"
          :data-tab-id="t.id"
          @click="select(t.id)"
        >
          <SlotRenderer
            v-if="t.header"
            :slotFn="t.header"
            :props="{ isActive: t.id === activeId, index: t.index }"
          />
          <template v-else>
            {{ t.label ?? 'Tab ' + (t.index + 1) }}
          </template>
        </Tab>
      </TabList>

      <div class="p-tabpanels">
        <template v-if="props.lazy">
          <template v-if="props.keepMounted">
            <div
              v-for="t in tabs"
              :key="t.id + '-panel'"
              :class="['p-tabpanel', t.id === activeId ? 'tab-open' : 'tab-closed']"
              :data-testid="`contenttab-${props.dataKey}-panel-${t.id}`"
              :data-tab-id="t.id"
            >
              <template v-if="shouldRenderLazyContent(t.id)">
                <SlotRenderer
                  :slotFn="t.content"
                  :props="{ isActive: t.id === activeId, index: t.index }"
                />
              </template>
              <template v-else-if="t.id === activeId">
                <InlineSpinner
                  :active="lazyTabLoading"
                  text="messages.loadingTab"
                  @min-duration-complete="onSpinnerMinDurationComplete"
                />
              </template>
            </div>
          </template>
          <div
            v-else-if="activeTabEntry"
            :key="activeTabEntry.id + '-panel'"
            class="p-tabpanel tab-open"
            :data-testid="`contenttab-${props.dataKey}-panel-${activeTabEntry.id}`"
            :data-tab-id="activeTabEntry.id"
          >
            <template v-if="shouldShowActiveContent">
              <SlotRenderer
                :slotFn="activeTabEntry.content"
                :props="{ isActive: true, index: activeTabEntry.index }"
              />
            </template>
            <template v-else>
              <InlineSpinner
                :active="lazyTabLoading"
                text="messages.loadingTab"
                @min-duration-complete="onSpinnerMinDurationComplete"
              />
            </template>
          </div>
        </template>
        <template v-else>
          <div
            v-for="t in tabs"
            :key="t.id + '-panel'"
            :class="['p-tabpanel', t.id === activeId ? 'tab-open' : 'tab-closed']"
            :data-testid="`contenttab-${props.dataKey}-panel-${t.id}`"
            :data-tab-id="t.id"
          >
            <SlotRenderer
              :slotFn="t.content"
              :props="{ isActive: t.id === activeId, index: t.index }"
            />
          </div>
        </template>
      </div>
    </Tabs>

    <!-- Keep children mounted so they can register (do NOT use v-if here) -->
    <div v-show="false" aria-hidden="true">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * ContentTabs
 * - Uses PrimeVue Tabs/TabList/Tab for consistent look & feel.
 * - Persists active tab under the provided `dataKey` using your existing store.
 * - Children register themselves via <ContentTab>.
 * - No routing here — purely manual `dataKey`.
 */

import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  type PropType,
  provide,
  ref,
  watch,
} from 'vue';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import Tabs from 'primevue/tabs';

import { useSelectedTabsStore } from '@/stores/selectedSubcomponentTabs';

type SlotFn = ((props?: Record<string, any>) => any) | undefined;

type TabEntry = {
  id: string; // stable id for persistence (from <ContentTab name> or auto index)
  index: number; // order index
  header?: SlotFn; // header slot function
  content: SlotFn; // content slot function (required)
  label?: string; // optional fallback label
};

const props = defineProps<{
  dataKey: string; // key under which the active tab is stored in your existing store
  lazy?: boolean; // if true, only mount active tab panel
  keepMounted?: boolean; // keep already lazy-mounted panel content alive after first render
}>();

const selectedTabsStore = useSelectedTabsStore();

// registry filled by <ContentTab> children
const tabs = ref<TabEntry[]>([]);
const activeId = ref<string>('');

// internal counter to preserve insertion order
let counter = 0;

// Register/unregister API exposed to <ContentTab>
const registerTab = (payload: {
  id?: string;
  header?: SlotFn;
  content?: SlotFn;
  label?: string;
}) => {
  const id = payload.id ?? String(counter);
  const entry: TabEntry = {
    id,
    index: counter++,
    header: payload.header,
    content: payload.content,
    label: payload.label,
  };
  tabs.value.push(entry);
};

const unregisterTab = (id: string) => {
  tabs.value = tabs.value.filter((t) => t.id !== id);
};

provide('persisted-tabs:register', registerTab);
provide('persisted-tabs:unregister', unregisterTab);

const initActive = () => {
  const stored = selectedTabsStore.getSelectedTab(props.dataKey);
  const exists = tabs.value.some((t) => t.id === stored);
  if (stored && exists) {
    activeId.value = stored;
  } else {
    activeId.value = tabs.value[0]?.id ?? '';
  }
};

const select = (id: string) => {
  if (!tabs.value.some((t) => t.id === id)) return;
  activeId.value = id;
  selectedTabsStore.setSelectedTab(props.dataKey, id); // persist to existing store
};

// Re-init when the list of child tabs changes (on mount or dynamic v-if)
watch(
  tabs,
  async (v) => {
    if (!v.length) return;
    await nextTick();
    if (!activeId.value || !v.some((t) => t.id === activeId.value)) {
      initActive();
    }
  },
  { deep: true },
);

onMounted(async () => {
  await nextTick(); // ensure children had a chance to register
  if (tabs.value.length) initActive();
});

// --- Lazy tab logic ---
const mountedTabs = ref<Record<string, boolean>>({});
const lazyTabLoading = ref(false);
const pendingTabId = ref<string | null>(null);
const tabReady = ref(false);
const spinnerReady = ref(false);
let loadCycleToken = 0;

const activeTabEntry = computed(() => {
  return tabs.value.find((t) => t.id === activeId.value) || null;
});

const shouldShowActiveContent = computed(() => {
  const id = activeId.value;
  if (!id) return false;
  if (!props.lazy) return true;
  if (mountedTabs.value[id]) return true;
  return pendingTabId.value === id && tabReady.value && spinnerReady.value;
});

const shouldRenderLazyContent = (tabId: string) => {
  if (!props.lazy) return true;
  return mountedTabs.value[tabId] || (tabId === activeId.value && shouldShowActiveContent.value);
};

const finalizePendingTab = () => {
  const id = pendingTabId.value;
  if (!id) return;
  if (id !== activeId.value) return;
  if (!tabReady.value || !spinnerReady.value) return;

  mountedTabs.value[id] = true;
  pendingTabId.value = null;
};

const onSpinnerMinDurationComplete = () => {
  spinnerReady.value = true;
  finalizePendingTab();
};

watch(
  () => activeId.value,
  async (id) => {
    if (!props.lazy) return;
    if (!id) return;

    loadCycleToken += 1;
    const cycleToken = loadCycleToken;

    if (mountedTabs.value[id]) {
      pendingTabId.value = null;
      tabReady.value = true;
      spinnerReady.value = true;
      lazyTabLoading.value = false;
      return;
    }

    pendingTabId.value = id;
    tabReady.value = false;
    spinnerReady.value = false;

    // If not yet mounted, show loader briefly
    lazyTabLoading.value = true;
    await nextTick();
    setTimeout(() => {
      if (cycleToken !== loadCycleToken) return;
      if (pendingTabId.value !== id) return;

      tabReady.value = true;
      lazyTabLoading.value = false;
      finalizePendingTab();
    }, 250); // Simulate async load, adjust as needed
  },
  { immediate: true },
);

onMounted(() => {
  if (props.lazy && activeId.value) {
    mountedTabs.value[activeId.value] = true;
  }
});

// Helper to render slot functions
const SlotRenderer = defineComponent({
  name: 'SlotRenderer',
  props: {
    slotFn: { type: Function as PropType<SlotFn>, default: undefined },
    props: {
      type: Object as PropType<Record<string, any>>,
      required: false,
      default: () => ({}),
    },
  },
  setup(p) {
    return () => (p.slotFn ? p.slotFn(p.props) : null);
  },
});
</script>

<style scoped lang="css">
@layer app {
  .p-tabs {
    background: transparent;
    overflow-x: hidden;
  }
  /* :deep() */
  .p-tablist-tab-list {
    background: transparent;
  }
  .p-tabpanels {
    background: transparent;
  }

  /* Keep panels mounted; just collapse visually */
  .tab-closed {
    position: relative;
    overflow: hidden;
    height: 0;
  }
}
</style>
